import React from 'react';
import { ChevronUp, ChevronDown, LayoutGrid, Square, SkipForward, SkipBack } from 'lucide-react';
import { useTheme } from '../styles/ThemeContext';
import { InteriorMenuItem } from '../../types/interiorConfig';
interface InteriorBottomBarProps {
  modelName: string;
  customizationPercent: number;
  sidebarOpen?: boolean;
  leftOffset?: number;
  // Integrated navigation controls
  showNavigation?: boolean;
  onToggleNavigation?: () => void;
  showRoomsPanel?: boolean;
  onToggleRooms?: () => void;
  // Tour-specific props
  isGuidedTourActive?: boolean;
  tourRoomIndex?: number;
  totalTourRooms?: number;
  currentTourRoomName?: string;
  onNextTourRoom?: () => void;
  onPrevTourRoom?: () => void;
  onStopTour?: () => void;
  // Step-by-Step props (removed from here, but keeping in interface for now to avoid breaking other files immediately)
  isStepByStep?: boolean;
  stepIndex?: number;
  totalSteps?: number;
  currentStepItem?: InteriorMenuItem | null;
  currentStepCategory?: string;
  onNextStep?: () => void;
  onPrevStep?: () => void;
  onToggleStepMode?: () => void;
}
export function InteriorBottomBar({
  modelName,
  customizationPercent,
  sidebarOpen = false,
  leftOffset,
  showNavigation,
  onToggleNavigation,
  showRoomsPanel,
  onToggleRooms,
  isGuidedTourActive = false,
  tourRoomIndex = 0,
  totalTourRooms = 0,
  currentTourRoomName = '',
  onNextTourRoom,
  onPrevTourRoom,
  onStopTour
}: InteriorBottomBarProps) {
  const {
    isDark
  } = useTheme();
  const hasNavControls = onToggleNavigation && onToggleRooms;
  const computedLeft = leftOffset !== undefined ? leftOffset : sidebarOpen ? 260 : 0;
  // ── GUIDED TOUR MODE ──
  if (isGuidedTourActive) {
    const tourProgress = totalTourRooms > 0 ? (tourRoomIndex + 1) / totalTourRooms * 100 : 0;
    return <div style={{
      left: 100
    }} className={`absolute bottom-0 right-0 z-20 backdrop-blur-xl border-t transition-all duration-[400ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${isDark ? 'bg-slate-900/90 border-cyan-500/20' : 'bg-white/90 border-teal-500/20'}`} data-id="element-913">
        {/* Tour Progress Bar (top edge) */}
        <div className={`absolute top-0 left-0 h-[3px] w-full ${isDark ? 'bg-white/5' : 'bg-gray-200'}`} data-id="element-914">
          <div className={`h-full transition-all duration-700 ease-out rounded-r-full ${isDark ? 'bg-gradient-to-r from-cyan-500 to-cyan-400 shadow-[0_0_12px_rgba(6,182,212,0.6)]' : 'bg-gradient-to-r from-teal-600 to-teal-500 shadow-[0_0_12px_rgba(13,148,136,0.4)]'}`} style={{
          width: `${tourProgress}%`
        }} data-id="element-915" />
        </div>

        <div className="flex items-center justify-between px-6 h-[60px]" data-id="element-916">
          {/* Left: Live indicator + Tour label */}
          <div className="flex items-center gap-4" data-id="element-917">
            <div className="flex items-center gap-2.5" data-id="element-918">
              <span className="relative flex h-2.5 w-2.5" data-id="element-919">
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${isDark ? 'bg-cyan-400' : 'bg-teal-500'}`} data-id="element-920" />
                <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${isDark ? 'bg-cyan-400' : 'bg-teal-500'}`} data-id="element-921" />
              </span>
              <span className={`text-[10px] font-bold uppercase tracking-[0.2em] ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-922">
                Guided Tour
              </span>
            </div>
            <div className={`h-6 w-px ${isDark ? 'bg-white/10' : 'bg-gray-200'}`} data-id="element-923" />
            <span className={`text-sm font-medium ${isDark ? 'text-white/50' : 'text-gray-500'}`} data-id="element-924">
              {modelName}
            </span>
          </div>

          {/* Center: Room navigation */}
          <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-3" data-id="element-925">
            {/* Prev Room */}
            <button onClick={onPrevTourRoom} disabled={tourRoomIndex === 0} className={`p-2 rounded-full transition-all ${tourRoomIndex === 0 ? 'opacity-30 cursor-not-allowed' : isDark ? 'hover:bg-white/10 text-white' : 'hover:bg-gray-100 text-gray-900'}`} data-id="element-926">
              <SkipBack className="w-4 h-4" data-id="element-927" />
            </button>

            {/* Current Room Info */}
            <div className="flex flex-col items-center min-w-[160px]" data-id="element-928">
              <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-929">
                {currentTourRoomName}
              </span>
              <span className={`text-[10px] mt-0.5 ${isDark ? 'text-white/40' : 'text-gray-400'}`} data-id="element-930">
                {tourRoomIndex + 1} of {totalTourRooms}
              </span>
            </div>

            {/* Next Room */}
            <button onClick={onNextTourRoom} disabled={tourRoomIndex === totalTourRooms - 1} className={`p-2 rounded-full transition-all ${tourRoomIndex === totalTourRooms - 1 ? 'opacity-30 cursor-not-allowed' : isDark ? 'hover:bg-white/10 text-white' : 'hover:bg-gray-100 text-gray-900'}`} data-id="element-931">
              <SkipForward className="w-4 h-4" data-id="element-932" />
            </button>
          </div>

          {/* Right: End Tour */}
          <button onClick={onStopTour} className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all ${isDark ? 'border-white/15 text-white/70 hover:bg-white/10 hover:text-white' : 'border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`} data-id="element-933">
            <Square className="w-3 h-3 fill-current" data-id="element-934" />
            <span className="text-xs font-bold uppercase tracking-wider" data-id="element-935">
              End Tour
            </span>
          </button>
        </div>
      </div>;
  }
  // ── DEFAULT MODE ──
  return <div style={{
    left: computedLeft
  }} className={`absolute bottom-0 right-0 z-20 backdrop-blur-xl transition-all duration-[400ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${isDark ? 'bg-slate-900/70 border-t border-white/10' : 'bg-white/70 border-t border-white/40'}`} data-id="element-936">
      <div className="flex items-center justify-between px-6 h-[50px]" data-id="element-937">
        {/* Left: Model Info */}
        <div className="flex items-center gap-6" data-id="element-938">
          {/* Builder Logo */}
          <img src="https://cdn.magicpatterns.com/uploads/fFcjkwzrHgtTNFTzYrB11c/landmark-logo-2.png" alt="Landmark Homes" className="h-7 w-auto" data-id="element-939" />
          <div className={`h-8 w-px ${isDark ? 'bg-white/10' : 'bg-gray-300/50'}`} data-id="element-940" />
          <div className="flex flex-col" data-id="element-941">
            <span className={`font-bold text-sm tracking-wide ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-942">
              {modelName}
            </span>
          </div>
          <div className={`h-8 w-px ${isDark ? 'bg-white/10' : 'bg-gray-300/50'}`} data-id="element-943" />
          <div className="flex items-center gap-2" data-id="element-944">
            <span className={`font-bold text-sm ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-945">
              {customizationPercent}%
            </span>
            <span className={`text-xs uppercase tracking-wider font-medium ${isDark ? 'text-white/40' : 'text-gray-500'}`} data-id="element-946">
              Customized
            </span>
          </div>
        </div>

        {/* Center: Empty now (Step-by-Step button removed) */}
        <div className="absolute left-1/2 -translate-x-1/2" data-id="element-947">
          {/* Space reserved for future use */}
        </div>

        {/* Right: Navigation + Rooms Controls */}
        <div className="flex items-center h-full" data-id="element-948">
          {hasNavControls && <div className={`flex items-center rounded-lg overflow-hidden border ${isDark ? 'border-white/10' : 'border-gray-200/60'}`} data-id="element-949">
              <button onClick={onToggleNavigation} className={`flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${showNavigation ? isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-white/50 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-950">
                {showNavigation ? <ChevronDown className="w-3.5 h-3.5" data-id="element-951" /> : <ChevronUp className="w-3.5 h-3.5" data-id="element-952" />}
                Navigation
              </button>
              <div className={`w-px self-stretch ${isDark ? 'bg-white/10' : 'bg-gray-200/60'}`} data-id="element-953" />
              <button onClick={onToggleRooms} className={`flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${showRoomsPanel ? isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-white/50 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-954">
                <LayoutGrid className="w-3.5 h-3.5" data-id="element-955" />
                Rooms
              </button>
            </div>}
        </div>
      </div>

      {/* Progress Bar */}
      <div className={`absolute top-0 left-0 h-[2px] w-full ${isDark ? 'bg-white/5' : 'bg-gray-200'}`} data-id="element-956">
        <div className={`h-full ${isDark ? 'bg-cyan-500 shadow-[0_0_10px_rgba(6,182,212,0.5)]' : 'bg-teal-600 shadow-[0_0_10px_rgba(13,148,136,0.3)]'}`} style={{
        width: `${customizationPercent}%`
      }} data-id="element-957" />
      </div>
    </div>;
}